/**
 * Tutorial Step Definitions
 *
 * Each page has an array of steps that guide the user through the interface.
 * Steps are displayed in order with spotlight effect and tooltips.
 */

export const TUTORIAL_STEPS = {
  /**
   * Exhibits Page (證物) - Judge: 魅嵐
   * 3 steps covering search, list, and tool usage
   */
  exhibits: [
    {
      id: 'search',
      target: '.Search__form > div',
      title: '搜尋證物',
      content: '請在搜尋框輸入「住院紀錄」，然後點擊「搜尋」按鈕來找到你的第一個證物！',
      position: 'bottom',
      spotlightPadding: 8,
      allowInteraction: true,
      hideNextButton: true,
      waitForAction: 'search' // Auto-advance when search is completed
    },
    {
      id: 'found-exhibit',
      target: '#found-exhibit-list',
      combineTarget: '.cate__content.found .detail',
      title: '找到證物了！',
      content: '太棒了！你找到了「住院紀錄」！點擊證物可以查看詳細描述，仔細閱讀可能會發現重要線索。',
      position: 'top',
      spotlightPadding: 16,
      allowInteraction: false,
      tooltipOffset: { top: -50 }
    },
    {
      id: 'go-to-souls',
      target: '.nav nav a:nth-child(3)',
      title: '前往亡魂頁面',
      content: '接下來，點擊靈陽判官的圖示，前往亡魂頁面與死者對話，獲取更多案件線索！',
      position: 'bottom',
      spotlightPadding: 4,
      spotlightBorderRadius: '50%',
      allowInteraction: true
    }
  ],

  /**
   * Souls Page (亡魂) - Judge: 靈陽
   * Steps covering map selection, conversation, and unlock memory
   */
  souls: [
    {
      id: 'position-map',
      target: '.soul-list',
      title: '選擇地點',
      content: '點擊地圖上的「獵戶座山莊．房間」圖示，與亡魂一起回到案發現場！',
      position: 'bottom',
      spotlightPadding: 12,
      allowInteraction: true,
      hideNextButton: true,
      waitForAction: 'select-position'
    },
    {
      id: 'conversation',
      target: '.show-conversation',
      title: '對話內容',
      content: '亡魂會在這裡回憶起在該地點發生的事情。仔細閱讀對話內容，可能藏有重要線索！',
      position: 'top',
      spotlightPadding: 8,
      allowInteraction: false
    },
    {
      id: 'options',
      target: '#options',
      title: '選擇問題',
      content: '選擇你想詢問的問題來繼續對話，直到出現「解鎖記憶」選項。',
      position: 'top',
      spotlightPadding: 8,
      allowInteraction: true,
      hideNextButton: true,
      waitForAction: 'unlock-memory-visible'
    },
    {
      id: 'unlock-memory',
      target: '#options a.option__link:last-child',
      title: '解鎖記憶',
      content: '點擊「解鎖記憶」來完成這個地點的探索，解鎖新的線索！',
      position: 'top',
      spotlightPadding: 8,
      allowInteraction: true,
      hideNextButton: true,
      waitForAction: 'unlock-memory'
    },
    {
      id: 'go-to-relateds',
      target: '.nav nav a:nth-child(1)',
      title: '前往關係人頁面',
      content: '接下來，點擊關係人圖示，前往關係人頁面訊問證人，獲取更多案件線索！',
      position: 'bottom',
      spotlightPadding: 4,
      spotlightBorderRadius: '50%',
      allowInteraction: true,
      tooltipOffset: { top: 20 }
    }
  ],

  /**
   * Relateds Page (關係人) - Judge: 奉書
   * Steps covering witness selection and testimony reading
   */
  relateds: [
    {
      id: 'witness-list',
      target: '#related-list li:first-child',
      title: '選擇證人',
      content: '點擊「王怡晨」來選擇你想訊問的對象。每位證人都可能掌握不同的案件線索！',
      position: 'bottom',
      spotlightPadding: 8,
      allowInteraction: true,
      hideNextButton: true,
      waitForAction: 'select-witness'
    },
    {
      id: 'testimony',
      target: '.Detail__list',
      title: '證詞內容',
      content: '這裡顯示證人的證詞。仔細閱讀每一段證詞，可能會發現關鍵線索！',
      position: 'top',
      spotlightPadding: 8,
      allowInteraction: false
    },
    {
      id: 'found-clue',
      target: '.tutorial-highlight-clue',
      title: '發現線索！',
      content: '你發現了一個重要的名字「周百樂」！這可能是另一位關係人，試著搜尋看看！',
      position: 'bottom',
      spotlightPadding: 4,
      allowInteraction: false,
      tooltipOffset: { top: 20 }
    },
    {
      id: 'search-witness',
      target: '.search-block',
      title: '搜尋關係人',
      content: '在搜尋框輸入「周百樂」，然後點擊搜尋按鈕來找到新的關係人！',
      position: 'bottom',
      spotlightPadding: 8,
      allowInteraction: true,
      hideNextButton: true,
      waitForAction: 'search-witness'
    },
    {
      id: 'found-new-witness',
      target: '#related-list',
      combineTarget: '.Detail__list',
      title: '找到新關係人！',
      content: '太棒了！你找到了「周百樂」！現在你可以查看他的證詞，發現更多線索！',
      position: 'top',
      spotlightPadding: 12,
      allowInteraction: false,
      tooltipOffset: { top: -30 }
    },
    {
      id: 'go-to-hints',
      target: '.nav nav a:nth-child(5)',
      title: '前往提示頁面',
      content: '接下來，點擊石獅圖示前往提示頁面，當你卡關時可以在那裡獲取提示！',
      position: 'bottom',
      spotlightPadding: 4,
      spotlightBorderRadius: '50%',
      allowInteraction: true,
      tooltipOffset: { top: 20 }
    }
  ],

  /**
   * Hints Page (提示) - Judge: 石獅
   * Final tutorial page - 2 steps covering hint unlock and content
   */
  hints: [
    {
      id: 'hint-unlock',
      target: '.detail:first-child',
      title: '獲取提示',
      content: '點擊提示區塊，給石獅一根潔牙骨就能獲得破案提示。提示會幫助你找到遺漏的線索！',
      position: 'bottom',
      spotlightPadding: 8,
      allowInteraction: true,
      hideNextButton: true,
      waitForAction: 'unlock-hint'
    },
    {
      id: 'hint-content',
      target: '.detail:first-child',
      title: '教學完成！',
      content: '恭喜你完成所有教學！現在你已經學會如何使用各項功能，開始破案吧！',
      position: 'bottom',
      spotlightPadding: 8,
      allowInteraction: false
    }
  ]
};

/**
 * Get steps for a specific page
 * @param {string} pageName - Page name (exhibits, hints, souls)
 * @returns {Array} Array of step definitions
 */
export function getStepsForPage(pageName) {
  return TUTORIAL_STEPS[pageName] || [];
}

/**
 * Get total step count for a page
 * @param {string} pageName - Page name
 * @returns {number} Number of steps
 */
export function getStepCount(pageName) {
  return getStepsForPage(pageName).length;
}

/**
 * Tutorial page names that have tutorials
 */
export const TUTORIAL_PAGES = ['exhibits', 'hints', 'souls', 'relateds'];

/**
 * Check if a page has tutorial
 * @param {string} pageName - Page name
 * @returns {boolean} True if page has tutorial
 */
export function hasTutorial(pageName) {
  return TUTORIAL_PAGES.includes(pageName);
}
